import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClock } from "@fortawesome/free-solid-svg-icons"
import { capitalize } from "lodash"
import { DisplayProvider } from "@/UI/DataDisplay/DisplayProvider/DisplayProvider"
import { IQuote } from "@/types/quotes.types"

import DeliveryOptionCardPickupDropoffETA from "./DeliveryOptionsCardPickupDropoff"
import DisplayPrice from "@/UI/DataDisplay/DisplayPrice"
import TrackingUnavailableMessage from "@/UI/Flags/TrackingUnavailableMessage"
import { AnimatePresence, motion } from "framer-motion"
import { Row } from "antd"

interface IDeliveryOptionCardProps {
  index: number
  quote: IQuote
  selectedQuote: IQuote | null | undefined
  onSelect: (quote: IQuote | null) => Promise<void>
  compact?: boolean
  isError?: boolean
  selectionDisabled?: boolean
}

const FullInterface: React.FC<{ driverId: string; quote: IQuote; isError?: boolean }> = ({
  driverId,
  quote,
  isError,
}) => {
  return (
    <AnimatePresence>
      <motion.span
        key={quote.id}
        className=""
        initial={{
          opacity: 0,
          x: 100,
          paddingTop: 0,
          paddingBottom: 0,
        }}
        animate={{
          minHeight: "auto",
          opacity: 1,
          x: 0,
        }}
      >
        <div className="flex flex-row pointer-events-none">
          <div className="flex flex-col">
            <DisplayProvider provider={quote.provider} driverId={driverId} />
            {!isError ? (
              <DeliveryOptionCardPickupDropoffETA
                type="pickup"
                deliveryType={quote.deliveryType}
                quote={quote}
              />
            ) : (
              <p>{capitalize(quote.error)}</p>
            )}
          </div>
          {!isError && (
            <div className="flex flex-col ml-auto p-0 text-right justify-between">
              <span className="my-0 font-semibold p-0 text-right">
                <DisplayPrice
                  isCents
                  currencyCode={quote.priceBreakdown.currency}
                  amount={quote.priceBreakdown.total}
                  justify="end"
                />
              </span>
              <div className="flex flex-row justify-end mt-0.5">
                <FontAwesomeIcon
                  className="flex flex-shrink-0 h-3 text-gray-500 my-auto mr-1"
                  icon={faClock}
                />
                <DeliveryOptionCardPickupDropoffETA
                  type="dropoff"
                  deliveryType={quote.deliveryType}
                  quote={quote}
                />
              </div>
            </div>
          )}
        </div>
      </motion.span>
    </AnimatePresence>
  )
}

const CompactInterface: React.FC<{
  driverId: string
  quote: IQuote
  isError?: boolean
  index: number
}> = ({ driverId, quote, isError, index }) => {
  return (
    // <div className="flex pointer-events-none items-center text-sm w-full select-none justify-between py-3">
    <AnimatePresence>
      <motion.div
        key={quote.id}
        className="flex pointer-events-none items-center text-sm w-full select-none justify-between"
        initial={{
          maxHeight: 0,
          opacity: 0,
          x: 60,
          paddingTop: 0,
          paddingBottom: 0,
        }}
        animate={{
          minHeight: "auto",
          maxHeight: 100,
          opacity: 1,
          x: 0,
          transition: {
            duration: 0.5 + 0.1 * index,
          },
        }}
      >
        {isError ? (
          <>
            <DisplayProvider provider={quote.provider} driverId={driverId} compact />
            <p>{capitalize(quote.error)}</p>
          </>
        ) : (
          <div className="flex flex-col w-full gap-y-1">
            <Row justify={"space-between"} className="w-full" align={"middle"}>
              <DisplayProvider provider={quote.provider} driverId={driverId} compact />
              <span className="my-0 font-semibold p-0">
                <DisplayPrice currencyCode={quote.currency} amount={quote.price} />
              </span>
            </Row>
            <div className="flex w-full justify-between gap-x-2">
              <div className="whitespace-nowrap">
                <DeliveryOptionCardPickupDropoffETA
                  type="pickup"
                  deliveryType={quote.deliveryType}
                  quote={quote}
                />
              </div>
              <div className="flex flex-row gap-x-1 justify-end whitespace-nowrap">
                <FontAwesomeIcon
                  className="flex flex-shrink-0 h-3 text-gray-500 my-auto"
                  icon={faClock}
                />
                <DeliveryOptionCardPickupDropoffETA
                  type="dropoff"
                  deliveryType={quote.deliveryType}
                  quote={quote}
                />
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  )
}

const DeliveryOptionCard: React.FC<IDeliveryOptionCardProps> = ({
  compact = false,
  isError,
  quote,
  selectedQuote,
  onSelect,
  index,
  selectionDisabled,
}) => {
  const driverId = quote.serviceName ? capitalize(quote.serviceName) : capitalize(quote.vehicleType)
  return (
    <div
      key={index}
      className={`${
        compact ? "mb-1" : "mb-3"
      } cursor-pointer rounded-lg border py-2 px-3 pointer-events-auto 
      ${quote.id === selectedQuote?.id ? "card-selected" : ""}
      ${isError || selectionDisabled ? "bg-kosmoLightGray cursor-not-allowed" : ""}`}
      onClick={() => {
        if (!isError && !selectionDisabled) {
          onSelect(quote)
        }
      }}
    >
      {compact ? (
        <CompactInterface driverId={driverId} quote={quote} isError={isError} index={index} />
      ) : (
        <FullInterface driverId={driverId} quote={quote} isError={isError} />
      )}
      <TrackingUnavailableMessage provider={quote.provider} />
    </div>
  )
}

export default DeliveryOptionCard
